import { useState, useCallback, useEffect, useRef } from "react";
import {
	addDays,
	subDays,
	parseISO,
	startOfDay,
	endOfDay,
	differenceInDays,
	isWithinInterval,
	isSameDay,
	format,
} from "date-fns";
import { useAuth } from "../contexts/AuthContext";

const CACHE_KEY = "calendar_events_cache";
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 1 week

export const useCalendarData = () => {
	const [allEvents, setAllEvents] = useState([]);
	const [filteredEvents, setFilteredEvents] = useState([]);
	const [currentViewDate, setCurrentViewDate] = useState(new Date());
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const { tokens, isAuthenticated, user, login, logout } = useAuth();
	const initialLoadComplete = useRef(false);
	const setupLock = useRef(false);
	const isInitialized = useRef(false);
	const [shouldStopWatchOnLogout, setShouldStopWatchOnLogout] = useState(true);

	useEffect(() => {
		if (!isAuthenticated) {
			setShouldStopWatchOnLogout(true);
		} else {
			setShouldStopWatchOnLogout(false);
		}
	}, [isAuthenticated]);

	// Track our date ranges
	const dateRangeRef = useRef({
		start: null,
		end: null,
		referenceDate: null,
	});

	const fetchInProgress = useRef(false);
	const pendingFetchRef = useRef(null);

	const saveToCache = useCallback((events, timeMin, timeMax) => {
		const cacheData = {
			events,
			timeMin,
			timeMax,
			timestamp: Date.now(),
		};
		localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
	}, []);

	const getFromCache = useCallback(() => {
		try {
			const cached = localStorage.getItem(CACHE_KEY);
			if (!cached) return null;

			const { events, timeMin, timeMax, timestamp } = JSON.parse(cached);

			// Check if cache is expired
			if (Date.now() - timestamp > CACHE_EXPIRY) {
				localStorage.removeItem(CACHE_KEY);
				return null;
			}

			// Parse dates in the cached events
			const parsedEvents = events.map((event) => ({
				...event,
				start: parseISO(event.start),
				end: parseISO(event.end),
			}));

			return {
				events: parsedEvents,
				timeMin: parseISO(timeMin),
				timeMax: parseISO(timeMax),
			};
		} catch (error) {
			console.error("Error reading from cache:", error);
			localStorage.removeItem(CACHE_KEY);
			return null;
		}
	}, []);

	const filterEventsForDate = useCallback((events, date) => {
		if (!date) return [];

		const viewStart = startOfDay(subDays(date, 100));
		const viewEnd = endOfDay(addDays(date, 100));

		const filtered = events.filter((event) => {
			if (!event.start || !event.end) return false;

			const eventStart =
				event.start instanceof Date ? event.start : parseISO(event.start);
			const eventEnd =
				event.end instanceof Date ? event.end : parseISO(event.end);

			if (event.allDay) {
				return isSameDay(eventStart, date);
			}

			return (
				isWithinInterval(eventStart, { start: viewStart, end: viewEnd }) ||
				isWithinInterval(eventEnd, { start: viewStart, end: viewEnd }) ||
				(eventStart <= viewStart && eventEnd >= viewEnd)
			);
		});
		return filtered;
	}, []);

	const isDateInLoadedRange = useCallback((date) => {
		if (!dateRangeRef.current.start || !dateRangeRef.current.end) {
			return false;
		}

		const targetDate = startOfDay(date);
		return isWithinInterval(targetDate, {
			start: dateRangeRef.current.start,
			end: dateRangeRef.current.end,
		});
	}, []);

	useEffect(() => {
		const filtered = filterEventsForDate(allEvents, currentViewDate);
		setFilteredEvents(filtered);
	}, [allEvents, currentViewDate, filterEventsForDate]);

	const updateCurrentView = useCallback((date) => {
		if (!date) return;
		const normalizedDate = startOfDay(date);
		setCurrentViewDate(normalizedDate);
	}, []);

	const fetchEvents = useCallback(
		async (targetDate = new Date(), isInitialLoad = false) => {
			if (fetchInProgress.current) {
				pendingFetchRef.current = { targetDate, isInitialLoad };
				return;
			}

			if (!isAuthenticated || !tokens) {
				console.log("Not authenticated or no tokens");
				return;
			}

			let shouldShowLoading = false;

			try {
				fetchInProgress.current = true;

				const timeMin = startOfDay(subDays(targetDate, 100));
				const timeMax = endOfDay(addDays(targetDate, 100));

				// Check cache on initial load
				if (isInitialLoad) {
					const cachedData = getFromCache();
					if (cachedData) {
						// If we have valid cached data, use it immediately
						setAllEvents(cachedData.events);
						dateRangeRef.current = {
							start: cachedData.timeMin,
							end: cachedData.timeMax,
							referenceDate: targetDate,
						};
						updateCurrentView(targetDate);
						initialLoadComplete.current = true;

						// Still fetch fresh data, but don't show loading
						shouldShowLoading = false;
					} else {
						// No cache, show loading on initial fetch
						shouldShowLoading = true;
					}
				} else {
					// Show loading only if fetching outside current range
					shouldShowLoading = !isDateInLoadedRange(targetDate);
				}

				if (shouldShowLoading) {
					setLoading(true);
				}

				updateCurrentView(targetDate);

				const response = await fetch(
					"https://api.my-cron.com/api/calendar/events",
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							tokens,
							timeMin: timeMin.toISOString(),
							timeMax: timeMax.toISOString(),
						}),
					}
				);

				if (response.status === 401) {
					console.log("Authentication failed, logging out");
					logout();
					return;
				}

				if (!response.ok) {
					throw new Error(
						`Failed to fetch calendar events: ${response.status}`
					);
				}

				const data = await response.json();

				if (data.updatedTokens) {
					await login({
						tokens: data.updatedTokens,
						user,
					});
				}

				const formattedEvents = (data.events || [])
					.map((event) => {
						try {
							const start = event.start ? parseISO(event.start) : null;
							const end = event.end ? parseISO(event.end) : null;

							if (!start || !end) return null;

							return {
								id: event.id,
								title: event.title || "Untitled Event",
								description: event.description,
								start,
								end,
								allDay: event.allDay || false,
								colorId: event.colorId,
								attendees: event.attendees || [],
								eventType: event.eventType || "default",
								location: event.location || "",
								organizer: event.organizer || null,
								reminders: event.reminders || null,
								status: event.status || "confirmed",
								htmlLink: event.htmlLink || "",
								creator: event.creator || null,
								conferenceData: event.conferenceData || null,
							};
						} catch (err) {
							console.error("Error processing event:", err);
							return null;
						}
					})
					.filter(Boolean);

				// Update our loaded date range
				dateRangeRef.current = {
					start: timeMin,
					end: timeMax,
					referenceDate: targetDate,
				};

				// Save to cache
				saveToCache(
					formattedEvents,
					timeMin.toISOString(),
					timeMax.toISOString()
				);

				setAllEvents(formattedEvents);

				if (isInitialLoad) {
					initialLoadComplete.current = true;
				}
			} catch (err) {
				console.error("Error in fetchEvents:", err);
				setError(err.message);

				// If error occurs on initial load and we have cache, keep using it
				if (isInitialLoad) {
					const cachedData = getFromCache();
					if (cachedData) {
						setAllEvents(cachedData.events);
						dateRangeRef.current = {
							start: cachedData.timeMin,
							end: cachedData.timeMax,
							referenceDate: targetDate,
						};
					}
				}
			} finally {
				if (shouldShowLoading) {
					setLoading(false);
				}
				fetchInProgress.current = false;

				if (pendingFetchRef.current) {
					const { targetDate, isInitialLoad } = pendingFetchRef.current;
					pendingFetchRef.current = null;
					fetchEvents(targetDate, isInitialLoad);
				}
			}
		},
		[
			isAuthenticated,
			tokens,
			login,
			logout,
			user,
			updateCurrentView,
			isDateInLoadedRange,
			getFromCache,
			saveToCache,
		]
	);

	const updateEvent = useCallback(
		async (updatedEvent) => {
			if (!isAuthenticated || !tokens) {
				console.log("Not authenticated or no tokens");
				return;
			}

			try {
				const response = await fetch(
					`https://api.my-cron.com/api/calendar/events/${updatedEvent.id}`,
					{
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							tokens,
							event: {
								title: updatedEvent.title,
								description: updatedEvent.description,
								start: updatedEvent.start,
								end: updatedEvent.end,
								allDay: updatedEvent.allDay,
								colorId: updatedEvent.colorId,
							},
						}),
					}
				);

				if (response.status === 401) {
					console.log("Authentication failed, logging out");
					logout();
					return;
				}

				if (!response.ok) {
					throw new Error(`Failed to update event: ${response.status}`);
				}

				const { event: eventData, updatedTokens } = await response.json();

				if (updatedTokens) {
					await login({
						tokens: updatedTokens,
						user,
					});
				}

				// Format the returned event
				const formattedEvent = {
					...eventData,
					start: parseISO(eventData.start),
					end: parseISO(eventData.end),
				};

				// Update local state
				setAllEvents((currentEvents) =>
					currentEvents.map((event) =>
						event.id === formattedEvent.id ? formattedEvent : event
					)
				);

				return formattedEvent;
			} catch (err) {
				console.error("Error in updateEvent:", err);
				setError(err.message);
				throw err;
			}
		},
		[isAuthenticated, tokens, login, logout, user]
	);

	const deleteEvent = useCallback(
		async (eventId) => {
			if (!isAuthenticated || !tokens) {
				console.log("Not authenticated or no tokens");
				return;
			}

			try {
				const response = await fetch(
					`https://api.my-cron.com/api/calendar/events/${eventId}`,
					{
						method: "DELETE",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({ tokens }),
					}
				);

				if (response.status === 401) {
					console.log("Authentication failed, logging out");
					logout();
					return;
				}

				if (!response.ok) {
					throw new Error(`Failed to delete event: ${response.status}`);
				}

				const { updatedTokens } = await response.json();

				if (updatedTokens) {
					await login({
						tokens: updatedTokens,
						user,
					});
				}

				// Update local state
				setAllEvents((currentEvents) =>
					currentEvents.filter((event) => event.id !== eventId)
				);
			} catch (err) {
				console.error("Error in deleteEvent:", err);
				setError(err.message);
				throw err;
			}
		},
		[isAuthenticated, tokens, login, logout, user]
	);

	const checkLoadMore = useCallback(
		(date) => {
			if (!dateRangeRef.current.start || !dateRangeRef.current.end) return;

			const bufferDays = 5;
			const currentRef = dateRangeRef.current.referenceDate;

			if (Math.abs(differenceInDays(date, currentRef)) > 15) {
				fetchEvents(date, false);
				return;
			}

			const pastThreshold = addDays(dateRangeRef.current.start, bufferDays);
			const futureThreshold = subDays(dateRangeRef.current.end, bufferDays);

			if (date < pastThreshold || date > futureThreshold) {
				fetchEvents(date, false);
			}
		},
		[fetchEvents]
	);

	const wsRef = useRef(null);

	const setupCalendarWatch = useCallback(async () => {
		if (!isAuthenticated || !tokens || !user?.id) return;

		if (setupLock.current) {
			return;
		}

		try {
			setupLock.current = true;

			const response = await fetch(
				"https://api.my-cron.com/api/calendar/watch",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ tokens, userId: user.id }),
				}
			);

			if (!response.ok) {
				throw new Error("Failed to set up calendar watch on server");
			}

			const watchData = await response.json();

			localStorage.setItem(
				"calendarWatch",
				JSON.stringify({
					channelId: watchData.id,
					resourceId: watchData.resourceId,
					expiration: Date.now() + 7 * 24 * 60 * 60 * 1000,
					lastSetup: Date.now(),
				})
			);
		} catch (error) {
			console.error("Error setting up calendar watch:", error);
			localStorage.removeItem("calendarWatch");
		} finally {
			setupLock.current = false;
		}
	}, [isAuthenticated, tokens, user?.id]);

	// Initialize WebSocket connection
	const initializeWebSocket = useCallback(() => {
		if (!tokens?.access_token || !user?.id) return;

		// Close existing connection if any
		if (wsRef.current) {
			wsRef.current.close();
			wsRef.current = null;
		}

		const wsUrl = `wss://api.my-cron.com/ws?token=${encodeURIComponent(
			tokens.access_token
		)}&userId=${encodeURIComponent(user.id)}`;

		const ws = new WebSocket(wsUrl);

		// Cache for processed messages to avoid duplicates
		const processedMessages = new Map();
		const MESSAGE_CACHE_TTL = 60000; // 1 minute TTL for cache entries

		// Function to process WebSocket messages with duplicate check
		const handleWebSocketMessage = async (update) => {
			const messageKey = `${update.channelId}-${update.messageNumber}`;

			if (processedMessages.has(messageKey)) {
				console.log("Skipping duplicate message:", messageKey);
				return; // Skip if message was already processed
			}

			// Mark the message as processed
			processedMessages.set(messageKey, Date.now());

			// Remove message from cache after TTL to keep memory usage low
			setTimeout(() => processedMessages.delete(messageKey), MESSAGE_CACHE_TTL);

			// Handle calendar update messages specifically
			if (update.type === "calendar_update") {
				await new Promise((resolve) => setTimeout(resolve, 1000));

				if (isInitialized.current && !setupLock.current) {
					await fetchEvents(currentViewDate, false);
				}
			}
		};

		ws.onmessage = async (event) => {
			try {
				const update = JSON.parse(event.data);

				await handleWebSocketMessage(update); // Process the message with duplicate check
			} catch (error) {
				console.error("Error handling WebSocket message:", error);
			}
		};

		ws.onopen = () => {
			if (isAuthenticated && tokens && user?.id) {
				setupCalendarWatch();
			}
		};

		ws.onclose = () => {
			wsRef.current = null;
		};

		ws.onerror = (error) => {
			//console.error("WebSocket error:", error);
		};

		wsRef.current = ws;

		return () => {
			if (wsRef.current === ws) {
				ws.close();
				wsRef.current = null;
			}
		};
	}, [
		tokens,
		user,
		currentViewDate,
		fetchEvents,
		setupCalendarWatch,
		isAuthenticated,
	]);

	useEffect(() => {
		return () => {
			if (wsRef.current) {
				wsRef.current.close();
				wsRef.current = null;
			}
		};
	}, []);

	// useEffect(() => {
	// 	return () => {
	// 		const watchData = localStorage.getItem("calendarWatch");
	// 		if (watchData && tokens) {
	// 			const { channelId, resourceId } = JSON.parse(watchData);

	// 			fetch("https://api.my-cron.com/api/calendar/watch/stop", {
	// 				//fetch("http://localhost:5001/api/calendar/watch/stop", {
	// 				method: "POST",
	// 				headers: {
	// 					"Content-Type": "application/json",
	// 				},
	// 				body: JSON.stringify({
	// 					tokens,
	// 					channelId,
	// 					resourceId,
	// 				}),
	// 			}).catch(console.error);

	// 			localStorage.removeItem("calendarWatch");
	// 		}
	// 	};
	// }, [tokens]);

	useEffect(() => {
		let mounted = true;

		const init = async () => {
			if (!isAuthenticated || !tokens || !user?.id || isInitialized.current) {
				return;
			}

			try {
				isInitialized.current = true;
				setupLock.current = true;

				if (!wsRef.current) {
					initializeWebSocket();
				}

				if (wsRef.current) {
					const watchData = localStorage.getItem("calendarWatch");
					if (!watchData) {
						await setupCalendarWatch();
					} else {
						const parsed = JSON.parse(watchData);
						if (
							!parsed.expiration ||
							parsed.expiration <= Date.now() + 3600000
						) {
							await setupCalendarWatch();
						}
					}
				}
			} catch (error) {
				console.error("Initialization error:", error);
				if (mounted) {
					isInitialized.current = false;
				}
			} finally {
				if (mounted) {
					setupLock.current = false;
				}
			}
		};

		init();

		return () => {
			mounted = false;
			if (tokens && isInitialized.current && shouldStopWatchOnLogout) {
				const watchData = localStorage.getItem("calendarWatch");
				if (watchData) {
					const { channelId, resourceId } = JSON.parse(watchData);
					fetch("https://api.my-cron.com/api/calendar/watch/stop", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							tokens,
							channelId,
							resourceId,
							logout: true,
						}),
					}).catch(console.error);
					localStorage.removeItem("calendarWatch");
				}
				isInitialized.current = false;
				setupLock.current = false;
			}
		};
	}, [
		isAuthenticated,
		tokens,
		user?.id,
		initializeWebSocket,
		setupCalendarWatch,
		shouldStopWatchOnLogout,
	]);

	const createEvent = useCallback(
		async (eventData) => {
			if (!isAuthenticated || !tokens) {
				console.log("Not authenticated or no tokens");
				return;
			}

			try {
				// Map the incoming event data to match Google Calendar API format
				const formattedEventData = {
					summary: eventData.summary,
					description: eventData.description || "",
					start: {
						dateTime: eventData.start,
						timeZone: eventData.timezone,
					},
					end: {
						dateTime: eventData.end,
						timeZone: eventData.timezone,
					},
					allDay: eventData.allDay,
					attendees: Array.isArray(eventData.attendees)
						? eventData.attendees.map((email) => ({ email }))
						: [],
					location: eventData.location,
					conferencing: eventData.conferencing,
					recurrence: eventData.recurrence,
					attachments: eventData.attachments || [],
					supportsAttachments: true,
					reminders: eventData.reminders,
					visibility: eventData.visibility,
					transparency: eventData.status === "free" ? "transparent" : "opaque",
					guestsCanModify: eventData.guestsCanModify,
					guestsCanInviteOthers: eventData.guestsCanInviteOthers,
					guestsCanSeeOtherGuests: eventData.guestsCanSeeOtherGuests,
					sendUpdates: eventData.sendUpdates,
				};

				const response = await fetch(
					"https://api.my-cron.com/api/calendar/events/create",
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							tokens,
							event: formattedEventData,
						}),
					}
				);

				if (response.status === 401) {
					console.log("Authentication failed, logging out");
					logout();
					return;
				}

				if (!response.ok) {
					throw new Error(`Failed to create event: ${response.status}`);
				}

				const { event: newEvent, updatedTokens } = await response.json();

				if (updatedTokens) {
					await login({
						tokens: updatedTokens,
						user,
					});
				}

				// Format the returned event
				const formattedEvent = {
					...newEvent,
					start: parseISO(newEvent.start.dateTime || newEvent.start.date),
					end: parseISO(newEvent.end.dateTime || newEvent.end.date),
				};

				// Update local state
				setAllEvents((currentEvents) => [...currentEvents, formattedEvent]);

				return formattedEvent;
			} catch (err) {
				console.error("Error in createEvent:", err);
				setError(err.message);
				throw err;
			}
		},
		[isAuthenticated, tokens, login, logout, user]
	);

	// Only run initial fetch once when authenticated
	useEffect(() => {
		if (isAuthenticated && tokens && !initialLoadComplete.current) {
			fetchEvents(new Date(), true);
		}
	}, [isAuthenticated, tokens, fetchEvents]);

	return {
		events: filteredEvents,
		loading,
		error,
		fetchEvents,
		createEvent,
		updateEvent,
		deleteEvent,
		checkLoadMore,
		setCurrentViewDate: updateCurrentView,
		currentViewDate,
	};
};
